import React, { Fragment, useState } from 'react'
import { Controller } from 'react-hook-form'
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from '@headlessui/react'
import PropTypes from 'prop-types'

// FPCC
import getIcon from 'common/utils/getIcon'
import ValidationError from 'components/Form/ValidationError'
import HelpText from 'components/Form/HelpText'
import FieldLabel from 'components/Form/FieldLabel'

function Autocomplete({
  control,
  disabled = false,
  errors,
  helpText,
  label = '',
  nameId,
  options,
}) {
  const [query, setQuery] = useState('')
  const getCurrentLabel = (currentValue) => {
    const currentOption = options?.find(({ value }) => value === currentValue)
    return currentOption?.label ? currentOption?.label : ''
  }
  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) =>
          option.label
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, '')),
        )

  return (
    <Fragment key="FormAutocomplete">
      <FieldLabel nameId={nameId} text={label} />
      <div className="w-full">
        <Controller
          control={control}
          defaultValue=""
          id={nameId}
          name={nameId}
          render={({ field: { value, onChange } }) => (
            <Combobox
              value={value}
              disabled={disabled}
              onChange={(e) => {
                onChange(e)
              }}
            >
              <div className="relative">
                <div className="relative w-full">
                  <ComboboxInput
                    className={`${
                      disabled ? 'opacity-50' : 'opacity-100'
                    } relative w-full cursor-default block border border-charcoal-200 rounded-lg py-2 px-3 focus:outline-hidden focus:ring-scarlet-800 focus:border-scarlet-800`}
                    displayValue={() => getCurrentLabel(value)}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Type to find a part of speech..."
                  />
                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                    {getIcon('ChevronUpDown', 'h-5 w-5 fill-current')}
                  </ComboboxButton>
                </div>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setQuery('')}
                >
                  <ComboboxOptions className="focus:outline-hidden absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/50">
                    {filteredOptions?.length === 0 && query !== '' ? (
                      <div className="relative cursor-default select-none py-2 px-4 text-charcoal-700">
                        Nothing found.
                      </div>
                    ) : (
                      filteredOptions?.map((option) => (
                        <ComboboxOption
                          key={option.value}
                          className={({ focus }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              focus
                                ? 'bg-scarlet-800 text-white'
                                : 'text-charcoal-900'
                            }`
                          }
                          value={option?.value}
                        >
                          {({ selected, focus }) => (
                            <>
                              <span
                                className={`inline-flex truncate ${
                                  selected ? 'font-medium' : 'font-normal'
                                }`}
                              >
                                {option.label}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    focus ? 'text-white' : 'text-scarlet-800'
                                  }`}
                                >
                                  {getIcon('Checkmark', 'h-5 w-5 fill-current')}
                                </span>
                              ) : null}
                            </>
                          )}
                        </ComboboxOption>
                      ))
                    )}
                  </ComboboxOptions>
                </Transition>
              </div>
            </Combobox>
          )}
        />
      </div>
      <HelpText text={helpText} />
      <ValidationError errors={errors} nameId={nameId} />
    </Fragment>
  )
}
// PROPTYPES
const { any, arrayOf, bool, object, shape, string } = PropTypes
Autocomplete.propTypes = {
  disabled: bool,
  errors: object,
  label: string,
  nameId: string.isRequired,
  options: arrayOf(shape({ label: string, value: any })),
  control: object,
  helpText: string,
}

export default Autocomplete
